import type {
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ForwardedRef,
  ReactNode,
} from 'react';
import { forwardRef, useState } from 'react';

import classNames from 'classnames';

import styles from './Checkbox.module.css';

import { Check } from '../../icons';
import { visuallyHidden } from '../../styles';
import { Text } from '../Text';

export interface CheckboxProps extends ComponentPropsWithoutRef<'input'> {
  label?: ReactNode;
  /** Display invalid state */
  invalid?: boolean;
  /** @deprecated The label should wrap automatically. */
  labelWrap?: never;
  /** @deprecated We don't support changing the color of the label anymore. */
  color?: never;
  /** Additional class name for checkbox text */
  labelClassName?: string;
}

/**
 * Render a styled checkbox input element.
 *
 * @param props - The {@link CheckboxProps} to render the checkbox.
 * @see https://corgi-x.tfd.engineering/components/checkbox
 */
export const Checkbox = forwardRef(Checkbox_);

function Checkbox_(
  { invalid, ...props }: CheckboxProps,
  ref?: ForwardedRef<HTMLInputElement>
) {
  const { label = '', ...checkboxProps } = props;

  const [hasFocus, setHasFocus] = useState(false);

  const handleFocus: FocusEventHandler<HTMLInputElement> = e => {
    setHasFocus(true);
    props.onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    setHasFocus(false);
    props.onBlur?.(e);
  };

  const cssClass = classNames(styles.container, props.className, {
    [styles.disabled]: checkboxProps.disabled,
    [styles.focus]: checkboxProps.disabled ? false : hasFocus,
    [styles.checked]: checkboxProps.checked,
    [styles.invalid]: invalid,
  });

  return (
    <label className={cssClass}>
      <input
        type="checkbox"
        ref={ref}
        checked={checkboxProps.checked}
        {...checkboxProps}
        className={visuallyHidden}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-invalid={invalid}
      />
      <span aria-hidden className={styles.checkbox}>
        <Check aria-hidden={true} fill="white" size={12} />
      </span>
      <Text className={classNames(styles.label, checkboxProps.labelClassName)}>
        {label}
      </Text>
    </label>
  );
}
